/**
 * MONSTER TOWN — TIME OF DAY. TIME-OF-DAY CREW (src/time/**).
 *
 * ONE `installTimeOfDay(ctx)` line in main.ts. From here this crew is a reader:
 * it asks `clock` what time it is and it changes what the game LOOKS and SOUNDS
 * like. It never moves the clock (only the bed does, see `sleep.ts`), it never
 * gates anything, and it cannot make anything fail.
 *
 * WHAT IT OWNS
 *   look.ts       the keyframe table — the whole cycle is data
 *   sky.ts        the backdrop canvas: gradient, stars, moon
 *   lights.ts     sun / bounce / warm fill / lit windows and bulbs
 *   ambience.ts   birds, air, crickets, an owl
 *   sleep.ts      the bed fade, and the bed's wire to the clock
 *
 * WHAT IT TOUCHES OUTSIDE ITSELF, and why that is all of it
 *   - `GameRenderer.setSunLook` / `setSunOffset` — two ADDITIVE hooks added to
 *     the renderer, whose defaults are the shipped constants.
 *   - `renderer.setAmbience(...)` — a call the world crew already makes.
 *   - `scene.background` and `scene.fog.color` — installed once, then mutated.
 *   - the shared `flat()` materials for window glass and lamp bulbs, restored
 *     exactly at daylight (see `lights.ts`).
 * No other crew's file is edited, and no other crew is imported.
 *
 * ENGINE ORDER 8. Inside the input/camera band and after `core.time` at 1, so
 * everything this frame — including the camera at 95 and the renderer behind it
 * — sees one consistent answer to "what does it look like right now".
 */

import type * as THREE from 'three'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { clock } from '../core/time'
import { world } from '../core/scene'
import type { GameContext } from '../app/context'
import {
  ambienceState,
  mixAt,
  nudgeAmbience,
  resetAmbienceCounts,
  seedAmbience,
  updateAmbience,
  type AmbienceMix,
} from './ambience'
import { applyHudNight, hudNightAlpha, hudNightBrightness, hudNightOpacity } from './hudNight'
import { applyLampGlow, lampGlowStats, rebuildLampGlow } from './lamps'
import {
  applyLook,
  applyNightDecals,
  applyNightMaterials,
  applyNightPaint,
  installLightRig,
  nightDecalColor,
  nightDecalCount,
  nightMaterialColors,
  nightPaintState,
  rescanNightDecals,
  rescanNightPaint,
  rescanSceneLights,
  trackedLightCount,
} from './lights'
import { blendLook, DAY_LOOK, lookAt, type DayLook } from './look'
import { skyBackdrop, skyNightLevel, skyRepaints, updateFog, updateSky } from './sky'
import { applyTint, installTint, tintStrength } from './tint'
import {
  forceSleepShow,
  holdSleepShow,
  installSleep,
  setBedBridge,
  sleepShowCount,
  updateSleepShow,
} from './sleep'

/**
 * HOW MUCH OF THE NIGHT A ROOM GETS. A room has no sky and no lamp posts, and
 * its light is almost entirely the hemisphere bounce — so applying the full
 * outdoor curve indoors does not read as "night", it reads as "the lights are
 * broken", and a three-year-old in a dark bedroom is exactly the outcome the
 * brief forbids. A room therefore sits most of the way back toward daylight:
 * cosy and warm after dark, still obviously evening, never gloomy.
 */
const INTERIOR_DAMP = 0.62

/** The stars breathe at ~3 Hz. Quantised: every distinct value is a repaint. */
const TWINKLE_HZ = 3

let ambienceAcc = 0
let twinkleTick = 0
let installed = false

function activeIsInterior(): boolean {
  const id = world.activeSceneId
  if (id === null) return false
  return world.get(id)?.def.kind === 'interior'
}

export function installTimeOfDay(ctx: GameContext): void {
  if (installed) return
  installed = true

  installLightRig(ctx.renderer)
  installTint(ctx.renderer.scene, ctx.camera.camera)
  installSleep()

  /**
   * OUR BACKDROP REPLACES THE WORLD CREW'S STATIC ONE, ONCE.
   *
   * `src/world/town.ts` sets `scene.background = skyTexture()` at boot — a baked
   * daytime gradient. From here on the backdrop is a canvas we repaint (see
   * sky.ts). The interiors crew swaps this object out on the way into a room and
   * puts it back on the way out, which keeps working because we never replace
   * the texture again — only the pixels inside it.
   */
  updateSky(lookAt(clock.t))
  ctx.renderer.scene.background = skyBackdrop()

  const rescan = (): void => {
    rescanSceneLights()
    rescanNightDecals()
    rescanNightPaint()
    rebuildLampGlow()
  }
  rescan()
  bus.on('app:booted', rescan)
  bus.on('scene:entered', rescan)
  bus.on('save:loaded', rescan)
  // A kid can place a lamp post of their own; it gets a pool of light like any
  // other. Debounced, because a QA sweep places sixteen things in a row.
  let lampsDirty = 0
  const soon = (): void => {
    lampsDirty = performance.now() + 400
  }
  bus.on('build:placed', soon)
  bus.on('build:removed', soon)
  bus.on('build:undo', soon)

  /**
   * The picture itself is read off the clock every frame rather than off an
   * event — a band edge is exactly where a subscriber would produce the pop the
   * whole file exists to avoid. What a band edge IS good for is the ambience
   * scheduler, which holds "the next bird is in nine seconds" and would
   * otherwise leave a brand new morning silent for the whole of it.
   */
  bus.on('time:phaseChanged', () => nudgeAmbience())

  /**
   * WHILE THE BED FADE IS RUNNING, THE SKY IS OURS EVEN INDOORS.
   *
   * A kid sleeps in their bedroom, so without this the one moment the star field
   * exists for would happen behind the interiors crew's cosy room backdrop and
   * nobody would ever see it. For the three seconds of the show the room floats
   * in the turning night sky; then whatever was there before goes back.
   */
  let borrowedBackground: THREE.Texture | THREE.Color | null = null
  let borrowing = false

  engine.addSystem({
    id: 'time.look',
    order: 8,
    fixed: (dt) => {
      // The ambience mixer runs at ~6 Hz, not 30: every call schedules Web Audio
      // ramps, and nothing in a breeze needs a sim step.
      ambienceAcc += dt
      if (ambienceAcc >= 1 / 6) {
        ambienceAcc = 0
        updateAmbience(clock.hour, lastQuiet)
      }
    },
    render: (_alpha, frameMs) => {
      const show = updateSleepShow(frameMs)
      lastQuiet = show.quiet

      const t = show.t ?? clock.t
      const outdoor = lookAt(t)
      const interior = activeIsInterior()
      // The show is a cinematic: it plays at full strength wherever it is seen.
      const look = interior && !show.active ? blendLook(outdoor, DAY_LOOK, INTERIOR_DAMP) : outdoor
      lastLook = look
      lastOutdoor = outdoor

      if (lampsDirty > 0 && performance.now() >= lampsDirty) {
        lampsDirty = 0
        rebuildLampGlow()
      }

      applyLook(ctx.renderer, look)
      applyLampGlow(outdoor.night)
      applyTint(ctx.camera.camera, look)
      // Windows and bulbs belong to the TOWN, so they follow the outdoor curve
      // whichever scene the kid happens to be standing in.
      applyNightMaterials(outdoor.night)
      // The world crew's painted contact shadows are unlit, so without this they
      // stop being shadows after dark and start being halos. See lights.ts.
      applyNightDecals(look.night)
      // ...and the build crew's "this ground is yours" ring is flat unlit paint
      // that never got darker at all. See the long note in lights.ts.
      applyNightPaint(look.night)
      // The HUD is the last thing in the frame that ignores the sky. It does not
      // move, it does not resize and nothing in it is ever disabled — see
      // hudNight.ts for why this is `opacity` and not a pane or a filter.
      applyHudNight(look.night, show.alpha)
      updateFog(ctx.renderer.scene, look)

      const scene = ctx.renderer.scene
      if (show.active) {
        if (!borrowing) {
          borrowing = true
          borrowedBackground = scene.background as THREE.Texture | THREE.Color | null
        }
        const sky = skyBackdrop()
        if (scene.background !== sky) scene.background = sky
      } else if (borrowing) {
        borrowing = false
        if (borrowedBackground !== null) scene.background = borrowedBackground
        borrowedBackground = null
      }

      // Repainting the sky while a room is covering it is pure waste, so the
      // twinkle clock only runs where the sky is actually on screen.
      if (!interior || show.active) {
        if (outdoor.night > 0.15) {
          twinkleTick = Math.floor(engine.now / (1000 / TWINKLE_HZ))
        }
        updateSky(outdoor, show.wheel, twinkleTick / TWINKLE_HZ)
      }
    },
  })

  installTimeDebug(ctx)
}

let lastLook: DayLook = DAY_LOOK
let lastOutdoor: DayLook = DAY_LOOK
let lastQuiet = 1

// ---------------------------------------------------------------------------
// debug surface
// ---------------------------------------------------------------------------

/**
 * `window.__time` — this crew's own QA handle, the same shape of thing as
 * `window.__world` and `window.__audio`. Deliberately NOT part of `HarnessApi`
 * (that interface is frozen); additive and read-only apart from the two test
 * hooks at the bottom, neither of which a kid can reach.
 */
export interface TimeDebugApi {
  /** What the world is being drawn with right now. */
  look(): DayLook & { t: number; hour: number; phase: string; interior: boolean }
  /** The look at any day fraction, without moving anything. Pure. */
  at(t: number): DayLook
  /** The OUTDOOR look, even when standing in a room. */
  outdoor(): DayLook
  /** 0..1 stars/lamps/lit-windows level as actually painted into the sky. */
  night(): number
  /** Opacity of the moonlight wash on the glass right now. */
  tint(): number
  /** The two shared "it is lit" materials, and the wash, at this instant. */
  materials(): { glass: number; bulb: number; tint: number }
  /** How many painted contact shadows the cycle is darkening, and to what. */
  decals(): { count: number; color: number; opacity: number }
  /** How many flat unlit highlights (the plot ring) are being walked down. */
  paint(): { count: number; color: number; base: number }
  /** What the HUD is being drawn at. 1 = untouched (daylight). */
  hud(): number
  /**
   * The two halves of that, separately. `bright` is the night dim (a filter);
   * `alpha` is the bed fade (opacity). AFTER DARK `alpha` MUST STAY 1 — the HUD
   * is dimmed, never made see-through. See hudNight.ts.
   */
  hudParts(): { bright: number; alpha: number }
  /** What an hour SOUNDS like. Pure — does not wait for the mixer to tick. */
  mix(hour: number): { air: number; birds: number; crickets: number; owls: number }
  skyRepaints(): number
  lights(): number
  /** How many lamps have a pool of light, in how many draw calls. */
  lamps(): ReturnType<typeof lampGlowStats>
  /** QA only: freeze the bed fade at a point on its timeline. */
  holdSleep(ms: number | null): void
  /**
   * QA only: stop/start the whole per-frame system, so a perf run can A/B what
   * the cycle costs inside ONE session on ONE machine. Turning it off puts the
   * world back to broad daylight rather than leaving it frozen mid-dusk.
   */
  setEnabled(on: boolean): void
  ambience(): { ready: boolean; mix: AmbienceMix; air: number; played: Record<string, number> }
  sleepShows(): number
  /** Run the bed fade without a bed. */
  playSleepShow(): void
  /** Is the fade pane up, and how opaque? */
  overlay(): number
  seed(n: number): void
  resetCounts(): void
  bedBridge(on: boolean): void
}

declare global {
  interface Window {
    __time?: TimeDebugApi
  }
}

function installTimeDebug(ctx: GameContext): void {
  window.__time = {
    look: () => ({
      ...lastLook,
      t: clock.t,
      hour: clock.hour,
      phase: clock.phase,
      interior: activeIsInterior(),
    }),
    at: (t) => lookAt(t),
    outdoor: () => lastOutdoor,
    night: () => skyNightLevel(),
    tint: () => tintStrength(),
    materials: () => ({ ...nightMaterialColors(), tint: tintStrength() }),
    decals: () => {
      const d = nightDecalColor()
      return { count: nightDecalCount(), color: d?.color ?? 0, opacity: d?.opacity ?? 0 }
    },
    paint: () => nightPaintState(),
    hud: () => hudNightOpacity(),
    hudParts: () => ({ bright: hudNightBrightness(), alpha: hudNightAlpha() }),
    mix: (hour) => mixAt(hour),
    skyRepaints: () => skyRepaints(),
    lights: () => trackedLightCount(),
    lamps: () => lampGlowStats(),
    holdSleep: (ms) => holdSleepShow(ms),
    setEnabled: (on) => {
      engine.setPaused('time.look', !on)
      if (!on) {
        applyLook(ctx.renderer, DAY_LOOK)
        applyNightMaterials(0)
        applyNightDecals(0)
        applyNightPaint(0)
        applyHudNight(0, 0)
        applyLampGlow(0)
        applyTint(ctx.camera.camera, DAY_LOOK)
        updateSky(DAY_LOOK)
      }
    },
    ambience: () => {
      const s = ambienceState()
      return { ready: s.ready, mix: s.mix, air: s.air, played: { ...s.played } }
    },
    sleepShows: () => sleepShowCount(),
    playSleepShow: () => forceSleepShow(),
    overlay: () => {
      // The fading part is the SKY PANE. The container is always at full
      // opacity so the eyelids can be genuinely opaque — see sleep.ts LID_SHARE.
      const el = document.getElementById('mt-sleep-pane') ?? document.getElementById('mt-sleep')
      return el === null ? 0 : Number(el.style.opacity || '0')
    },
    seed: (n) => seedAmbience(n),
    resetCounts: () => resetAmbienceCounts(),
    bedBridge: (on) => setBedBridge(on),
  }
}
